import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Settings } from "lucide-react";

import {
  fetchMyMeetupEvents,
  bucketEvent,
  autoDeleteAt,
  type MyMeetupEventRow,
} from "@/lib/api/my-events";
import { fetchEventById } from "@/lib/api/events";
import { fetchParticipantById } from "@/lib/api/participants";
import {
  clearParticipantSession,
  getLastParticipant,
  saveLastParticipant,
} from "@/lib/participant-session";
import { formatCountdown, msUntilReveal } from "@/lib/reveal";

import { Button, LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { VideoAvatar } from "@/components/ui/VideoAvatar";
import { Logo } from "@/components/ui/Logo";
import { Loader, EmptyState, ErrorBlock } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";
import { ParticipantShell } from "@/components/layout/ParticipantShell";
import { GlobalFooter } from "@/components/layout/GlobalFooter";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const FOUR_DAYS_MS = 4 * 86_400_000;

export function AccountHomePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Resolve which participantId we should enumerate from. Priority:
  //   1) ?pid=... in the URL (email magic link)
  //   2) localStorage `last-participant`
  // If neither is available we send the user to /join.
  const pidFromUrl = params.get("pid");
  const lastParticipant = pidFromUrl ? null : getLastParticipant();
  const participantId = pidFromUrl ?? lastParticipant?.participantId ?? "";

  // If the URL provided a pid, persist it as the new "last participant" pointer
  // so subsequent visits to /me without a pid still resolve.
  useEffect(() => {
    if (!pidFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchParticipantById(pidFromUrl);
        if (cancelled || !p) return;
        const event = await fetchEventById(p.event_id);
        saveLastParticipant({
          participantId: p.id,
          slug: event?.slug ?? "",
          name: p.name,
          email: p.email,
        });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pidFromUrl]);

  if (!participantId) {
    return (
      <MobileFrame>
        <div className="flex-1 px-4 py-10">
          <Card padding="lg" className="text-center">
            <EmptyState
              icon="🔑"
              title="No participant on this device"
              description="Open the link from your invitation email or scan the QR at the venue, then come back here."
              action={
                <LinkButton to="/join" variant="secondary">
                  Open scanner
                </LinkButton>
              }
            />
          </Card>
        </div>
      </MobileFrame>
    );
  }

  return <AccountHome participantId={participantId} navigate={navigate} />;
}

function AccountHome({
  participantId,
  navigate,
}: {
  participantId: string;
  navigate: (to: string) => void;
}) {
  const {
    data: events = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["my-meetup-events", participantId],
    queryFn: () => fetchMyMeetupEvents(participantId),
  });

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const buckets = useMemo(() => bucketize(events, now), [events, now]);
  const me = events[0]; // any row carries this person's name/role
  const summary = useMemo(() => summarize(events), [events]);
  const matchesCount = summary.totalMatches;

  // Pick the freshest pitch video for the hero — prefer a live/upcoming event,
  // fall back to the most recent past pitch.
  const heroVideo = useMemo(() => {
    const ordered = [...buckets.live, ...buckets.upcoming, ...buckets.past];
    const candidate = ordered.find(
      (e) => e.has_video && e.video_storage_path && e.pitch_status === "approved",
    );
    if (!candidate?.video_storage_path) return null;
    return {
      eventId: candidate.event_id,
      participantId: candidate.participant_id,
      path: candidate.video_storage_path,
    };
  }, [buckets]);

  const handleEventOpen = (e: MyMeetupEventRow) => {
    if (!e.slug) return;
    const bucket = bucketEvent(e, now);
    if (bucket === "live" || bucket === "upcoming") {
      navigate(`/e/${e.slug}/feed`);
    } else if (bucket === "past") {
      navigate(`/e/${e.slug}/wrap`);
    }
  };

  if (isLoading) {
    return (
      <ParticipantShell matchesCount={matchesCount}>
        <Loader label="Loading your events..." />
      </ParticipantShell>
    );
  }

  if (error) {
    return (
      <ParticipantShell matchesCount={matchesCount}>
        <ErrorBlock
          title="Could not load your events"
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      </ParticipantShell>
    );
  }

  if (events.length === 0) {
    return (
      <ParticipantShell matchesCount={matchesCount}>
        <Card padding="lg" className="text-center">
          <EmptyState
            icon="🌱"
            title="No events yet"
            description="Once you register for a Finally event, it will appear here. Open the QR or invitation link to get started."
            action={
              <LinkButton to="/join" variant="secondary">
                Scan event QR
              </LinkButton>
            }
          />
        </Card>
      </ParticipantShell>
    );
  }

  return (
    <ParticipantShell matchesCount={matchesCount}>
      <div className="animate-fade-in">
        <ProfileHero
          me={me}
          heroVideo={heroVideo}
          summary={summary}
          onEdit={() => {
            const live = buckets.live[0];
            const upcoming = buckets.upcoming[0];
            const target = live ?? upcoming;
            if (target?.slug) navigate(`/e/${target.slug}/profile-settings`);
          }}
          editEnabled={Boolean((buckets.live[0] ?? buckets.upcoming[0])?.slug)}
        />

        {buckets.live.length > 0 ? (
          <Section eyebrow="Happening now">
            {buckets.live.map((e) => (
              <LiveEventCard key={e.event_id} event={e} now={now} onOpen={() => handleEventOpen(e)} />
            ))}
          </Section>
        ) : null}

        {buckets.upcoming.length > 0 ? (
          <Section
            eyebrow={`Upcoming · ${buckets.upcoming.length} invited`}
          >
            {buckets.upcoming.map((e) => (
              <UpcomingCard key={e.event_id} event={e} onOpen={() => handleEventOpen(e)} />
            ))}
          </Section>
        ) : null}

        {buckets.past.length + buckets.cancelled.length > 0 ? (
          <Section eyebrow={`Past · ${buckets.past.length + buckets.cancelled.length} events`}>
            {buckets.past.map((e) => (
              <PastEventCard key={e.event_id} event={e} now={now} onOpen={() => handleEventOpen(e)} />
            ))}
            {buckets.cancelled.map((e) => (
              <CancelledCard key={e.event_id} event={e} />
            ))}
          </Section>
        ) : null}

        <SignOutSection />
      </div>
    </ParticipantShell>
  );
}

function SignOutSection() {
  const navigate = useNavigate();
  const handleSignOut = () => {
    const last = getLastParticipant();
    if (last?.slug) clearParticipantSession(last.slug);
    try {
      localStorage.removeItem("finally.meetup.last-participant");
    } catch {
      /* non-critical */
    }
    navigate("/");
  };
  return (
    <Card padding="md" className="mt-8">
      <p className="text-[14px] font-bold text-[#1a1a1a]">Sign out</p>
      <p className="mt-1 text-[13px] text-[#6c757d]">
        Sign out of this device. You can sign back in any time with the email you registered with.
      </p>
      <Button variant="secondary" onClick={handleSignOut} className="mt-3" size="md">
        Sign out
      </Button>
    </Card>
  );
}

function ProfileHero({
  me,
  heroVideo,
  summary,
  onEdit,
  editEnabled,
}: {
  me: MyMeetupEventRow | undefined;
  heroVideo: { eventId: string; participantId: string; path: string } | null;
  summary: { totalEvents: number; totalMatches: number; sinceLabel: string };
  onEdit: () => void;
  editEnabled: boolean;
}) {
  if (!me) return null;
  return (
    <div className="mb-5">
      <div className="flex items-center gap-3">
        <VideoAvatar
          name={extractName(me)}
          size={64}
          bordered
          videoStoragePath={heroVideo?.path}
          eventId={heroVideo?.eventId}
          viewerParticipantId={heroVideo?.participantId}
        />
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-bold text-[#1a1a1a]">{extractName(me)}</p>
          <p className="text-[12px] text-[#6c757d]">Your account</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Settings className="h-3.5 w-3.5" />}
          onClick={onEdit}
          disabled={!editEnabled}
        >
          Edit
        </Button>
      </div>
      <p className="mt-2 text-[11px] uppercase tracking-wide text-[#94a3b8]">
        {summary.totalEvents} {summary.totalEvents === 1 ? "event" : "events"} · {summary.totalMatches}{" "}
        {summary.totalMatches === 1 ? "match" : "matches"} · since {summary.sinceLabel}
      </p>
    </div>
  );
}

function Section({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
        {eyebrow}
      </p>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function LiveEventCard({
  event,
  now,
  onOpen,
}: {
  event: MyMeetupEventRow;
  now: Date;
  onOpen: () => void;
}) {
  const ms = msUntilReveal(
    { event_date: event.event_date, match_reveal_hours: event.match_reveal_hours },
    now,
  );
  const eventTime = new Date(event.event_date).getTime();
  const status =
    ms > 0
      ? `Pitch ${event.has_video ? "recorded" : "missing"} · matches reveal in ${formatCountdown(ms)}`
      : eventTime > now.getTime()
        ? "Matches live · event soon"
        : "Live now";
  return (
    <Card tone="approved" padding="md">
      <div className="flex items-start gap-3">
        <Badge tone="success">· Live</Badge>
        <p className="text-[12px] text-[#065f46] pt-[3px]">
          {formatEventDate(event.event_date)}
          {event.location ? ` · ${event.location}` : ""}
        </p>
      </div>
      <div className="mt-3 flex items-start gap-3">
        <EventMedia event={event} size={64} />
        <div className="flex-1 min-w-0">
          <h3 className="text-[18px] font-bold text-[#047857] tracking-tight">
            {event.title}
          </h3>
          <p className="mt-1 text-[13px] text-[#065f46]">{status}</p>
        </div>
      </div>
      <Button onClick={onOpen} className="mt-4" size="md" disabled={!event.slug}>
        Open event
      </Button>
      <p className="mt-2 text-[11px] text-[#94a3b8]">
        Likes you give before reveal count for mutual matches.
      </p>
    </Card>
  );
}

/**
 * Renders the participant's own pitch for an event as a click-to-play
 * round preview, falling back to the date block when no video is uploaded
 * yet. Each event in `/me` has its own pitch — this is the visual hook
 * that surfaces them per-row.
 */
function EventMedia({
  event,
  size = 48,
}: {
  event: MyMeetupEventRow;
  size?: number;
}) {
  if (event.has_video && event.video_storage_path) {
    return (
      <VideoAvatar
        name={event.title}
        size={size}
        bordered
        videoStoragePath={event.video_storage_path}
        eventId={event.event_id}
        viewerParticipantId={event.participant_id}
        showPlayBadge
      />
    );
  }
  return <DateBlock dateIso={event.event_date} muted={event.status === "cancelled"} />;
}

function UpcomingCard({
  event,
  onOpen,
}: {
  event: MyMeetupEventRow;
  onOpen: () => void;
}) {
  const ready = event.pitch_status === "approved";
  return (
    <Card padding="sm" hoverable className="cursor-pointer" >
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 text-left">
        <EventMedia event={event} />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-[#1a1a1a] truncate">{event.title}</p>
          <p className="text-[12px] text-[#6c757d] truncate">
            {formatEventDate(event.event_date)} · by {event.organizer_name}
          </p>
          <p
            className={
              "mt-0.5 text-[11px] font-semibold " +
              (ready
                ? "text-[#065f46]"
                : event.pitch_status === "pending"
                  ? "text-[#92400e]"
                  : "text-[#1d4ed8]")
            }
          >
            {ready
              ? "✓ Pitch ready"
              : event.pitch_status === "pending"
                ? "Record pitch to participate"
                : `Pitch ${event.pitch_status}`}
          </p>
        </div>
        <span className="text-[18px] text-[#94a3b8]">›</span>
      </button>
    </Card>
  );
}

function PastEventCard({
  event,
  now,
  onOpen,
}: {
  event: MyMeetupEventRow;
  now: Date;
  onOpen: () => void;
}) {
  const deletionDate = autoDeleteAt(event);
  const msToDeletion = deletionDate.getTime() - now.getTime();
  const showDeletion = msToDeletion > 0 && msToDeletion < FOUR_DAYS_MS;
  const matches = event.matches_count;

  return (
    <Card padding="sm" hoverable>
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 text-left">
        <EventMedia event={event} />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-[#1a1a1a] truncate">{event.title}</p>
          <p className="text-[12px] text-[#6c757d] truncate">
            {formatEventDate(event.event_date)} · {matches}{" "}
            {matches === 1 ? "match" : "matches"} · by {event.organizer_name}
          </p>
        </div>
        <span className="text-[18px] text-[#94a3b8]">›</span>
      </button>
      {showDeletion ? (
        <Card tone="flagged" padding="sm" className="mt-3 shadow-none">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">
            Auto-delete in {Math.ceil(msToDeletion / 86_400_000)}{" "}
            {Math.ceil(msToDeletion / 86_400_000) === 1 ? "day" : "days"}
          </p>
          <p className="mt-1 text-[12px] text-[#78350f]">
            Save contacts you want to keep — profiles and pitches are erased on{" "}
            {formatShortDate(deletionDate.toISOString())}.
          </p>
        </Card>
      ) : null}
    </Card>
  );
}

function CancelledCard({ event }: { event: MyMeetupEventRow }) {
  return (
    <Card padding="sm" className="opacity-70">
      <div className="flex items-center gap-3">
        <DateBlock dateIso={event.event_date} muted />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-[#475569] line-through truncate">
            {event.title}
          </p>
          <p className="text-[11px] font-semibold text-[#991b1b]">Cancelled by organizer</p>
        </div>
      </div>
    </Card>
  );
}

function DateBlock({ dateIso, muted }: { dateIso: string; muted?: boolean }) {
  const d = new Date(dateIso);
  return (
    <div
      className={
        "shrink-0 flex h-12 w-12 flex-col items-center justify-center rounded-[10px] border border-[#e2e8f0] bg-[#f8fafc] " +
        (muted ? "opacity-60" : "")
      }
    >
      <span className="text-[9px] font-semibold uppercase tracking-wide text-[#6c757d] leading-none">
        {MONTHS[d.getMonth()]}
      </span>
      <span className="mt-0.5 text-[18px] font-bold text-[#1a1a1a] leading-none">
        {d.getDate().toString().padStart(2, "0")}
      </span>
    </div>
  );
}

/* ----------------- helpers ----------------- */

function bucketize(events: MyMeetupEventRow[], now: Date) {
  const live: MyMeetupEventRow[] = [];
  const upcoming: MyMeetupEventRow[] = [];
  const past: MyMeetupEventRow[] = [];
  const cancelled: MyMeetupEventRow[] = [];
  for (const e of events) {
    const b = bucketEvent(e, now);
    if (b === "live") live.push(e);
    else if (b === "upcoming") upcoming.push(e);
    else if (b === "cancelled") cancelled.push(e);
    else past.push(e);
  }
  // upcoming asc by date (soonest first)
  upcoming.sort(
    (a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime(),
  );
  return { live, upcoming, past, cancelled };
}

function summarize(events: MyMeetupEventRow[]) {
  const totalMatches = events.reduce((sum, e) => sum + (e.matches_count ?? 0), 0);
  const totalEvents = events.length;
  const oldest = events.reduce<Date | null>((oldest, e) => {
    const d = new Date(e.registered_at);
    if (!oldest || d < oldest) return d;
    return oldest;
  }, null);
  const sinceLabel = oldest
    ? oldest.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "today";
  return { totalEvents, totalMatches, sinceLabel };
}

function extractName(row: MyMeetupEventRow): string {
  // RPC doesn't return our own name explicitly; reuse the participant's
  // session pointer if available. The list of events is authoritative for
  // identity but the row only has organizer_name. Fallback to "You".
  void row;
  const last = getLastParticipant();
  return last?.name ?? "You";
}

function formatEventDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

